import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';

interface LedgerRow {
  id: string;
  type: string;
  amount: number;
  description: string | null;
  created_at: string;
}

interface WalletData {
  balance: number;
  total_profit_loss: number;
}

const typeLabel: Record<string, string> = {
  deposit: 'Nạp tiền',
  withdraw: 'Rút tiền',
  buy: 'Mua vàng',
  sell: 'Bán vàng',
  fee: 'Phí giao dịch',
};

const typeStyle: Record<string, string> = {
  deposit: 'bg-primary-500/15 text-primary-400',
  withdraw: 'bg-accent-500/15 text-accent-400',
  buy: 'bg-emerald-500/15 text-emerald-400',
  sell: 'bg-rose-500/15 text-rose-400',
  fee: 'bg-foreground-500/15 text-foreground-400',
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function Assets() {
  const [ledger, setLedger] = useState<LedgerRow[]>([]);
  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [hasOrders, setHasOrders] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const balance = wallet?.balance || 0;
  const totalDeposit = ledger.filter((r) => r.type === 'deposit').reduce((sum, r) => sum + r.amount, 0);
  const totalWithdraw = ledger.filter((r) => r.type === 'withdraw').reduce((sum, r) => sum + Math.abs(r.amount), 0);
  const totalInvest = Math.max(0, totalDeposit - totalWithdraw);
  const profit = balance - totalInvest;
  const totalValue = balance;

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      try {
        setLoading(true);
        setError('');
        const { data: userData } = await supabase.auth.getUser();
        const userId = userData.user?.id;
        if (!userId) {
          if (!cancelled) setLoading(false);
          return;
        }

        // Fetch wallet + orders + ledger
        const [{ data: walletRaw }, { data: ordersRaw }, { data: ledgerRaw }] = await Promise.all([
          supabase.rpc('get_user_wallet_json', { p_user_id: userId }),
          supabase.rpc('get_user_orders_json', { p_user_id: userId }),
          supabase.rpc('get_user_ledger_json', { p_user_id: userId }),
        ]);

        const wallets = Array.isArray(walletRaw) ? walletRaw : JSON.parse((walletRaw as string) || '[]');
        const orders = Array.isArray(ordersRaw) ? ordersRaw : JSON.parse((ordersRaw as string) || '[]');
        const ledgers = Array.isArray(ledgerRaw) ? ledgerRaw : JSON.parse((ledgerRaw as string) || '[]');

        if (!cancelled) {
          setWallet(wallets[0] || null);
          setHasOrders(orders.length > 0);
          setLedger(ledgers.slice(0, 50));
        }
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Không thể tải dữ liệu');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, []);

  const emptyCard = (
    <div className="flex flex-col items-center justify-center py-10 md:py-14 rounded-xl border border-background-200/60 bg-background-100">
      <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-background-200 flex items-center justify-center mb-4">
        <i className="ri-coins-line text-xl md:text-2xl text-foreground-500"></i>
      </div>
      <p className="text-sm font-medium text-foreground-700">Chưa có tài sản nào</p>
      <p className="text-xs text-foreground-500 mt-1 text-center max-w-xs">Bắt đầu giao dịch để xây dựng danh mục vàng của bạn</p>
      <Link to="/giao-dich" className="mt-4 px-5 py-2.5 rounded-md bg-primary-500 text-background-950 text-sm font-semibold hover:bg-primary-600 whitespace-nowrap cursor-pointer transition-colors">
        Giao dịch ngay
      </Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 md:mb-6">Tài sản &amp; lịch sử giao dịch</h1>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-8">
            <div className="rounded-xl border border-background-200/60 bg-background-100 p-4 md:p-5">
              <p className="text-xs text-foreground-600">Tổng giá trị tài sản</p>
              <p className="mt-1 text-xl md:text-2xl font-bold text-gold-gradient whitespace-nowrap">{totalValue.toLocaleString('vi-VN')}đ</p>
            </div>
            <div className="rounded-xl border border-background-200/60 bg-background-100 p-4 md:p-5">
              <p className="text-xs text-foreground-600">Tổng vốn đầu tư</p>
              <p className="mt-1 text-xl md:text-2xl font-bold text-foreground-950 whitespace-nowrap">
                {totalInvest.toLocaleString('vi-VN')}đ
              </p>
            </div>
            <div className="rounded-xl border border-background-200/60 bg-background-100 p-4 md:p-5">
              <p className="text-xs text-foreground-600">Lợi nhuận</p>
              <p className={`mt-1 text-xl md:text-2xl font-bold whitespace-nowrap ${profit >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                {`${profit >= 0 ? '+' : ''}${profit.toLocaleString('vi-VN')}đ`}
              </p>
            </div>
          </div>

          <h2 className="text-lg font-semibold text-foreground-950 mb-4">Danh mục vàng đang nắm giữ</h2>
          {hasOrders ? (
            <div className="mb-10">{emptyCard}</div>
          ) : (
            <div className="mb-10">{emptyCard}</div>
          )}

          <h2 className="text-lg font-semibold text-foreground-950 mb-4">Lịch sử giao dịch</h2>

          {loading && (
            <div className="flex items-center justify-center py-10 md:py-14">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {!loading && error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 md:p-5 text-center">
              <p className="text-sm text-rose-600">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer transition-colors"
              >
                Thử lại
              </button>
            </div>
          )}

          {!loading && !error && ledger.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10 md:py-14 rounded-xl border border-background-200/60 bg-background-100">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-background-200 flex items-center justify-center mb-4">
                <i className="ri-file-list-3-line text-xl md:text-2xl text-foreground-500"></i>
              </div>
              <p className="text-sm font-medium text-foreground-700">Chưa có giao dịch nào</p>
              <p className="text-xs text-foreground-500 mt-1 text-center max-w-xs">Các giao dịch nạp tiền, rút tiền, mua bán vàng sẽ hiển thị tại đây sau khi hoàn tất</p>
            </div>
          )}

          {!loading && !error && ledger.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-background-200/60">
              <table className="w-full text-sm min-w-[680px]">
                <thead>
                  <tr className="bg-background-100 text-left">
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Loại</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Chi tiết</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600 text-right">Số tiền</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Thời gian</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.map((t) => (
                    <tr key={t.id} className="border-t border-background-200/40 hover:bg-background-100/60 transition-colors">
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${typeStyle[t.type] || 'bg-background-200 text-foreground-600'}`}>
                          {typeLabel[t.type] || t.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground-800">{t.description || '—'}</td>
                      <td className={`px-4 py-3 text-right font-semibold whitespace-nowrap ${t.amount >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                        {t.amount >= 0 ? '+' : ''}{Number(t.amount).toLocaleString('vi-VN')}đ
                      </td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{formatDate(t.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-6 flex justify-center">
            <Link to="/giao-dich" className="px-6 py-3 rounded-md bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-600 whitespace-nowrap cursor-pointer transition-colors">
              Giao dịch ngay
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}